// =============================================================================
// features/auth/FirstAccessPage.tsx — Tela de primeiro acesso (F56-S02).
//
// Quem entra pela primeira vez com a senha temporária cai aqui, sem menu e sem barra
// do sistema, e define o e-mail próprio e a senha definitiva. Só depois o sistema abre.
//
// Segurança (LGPD doc 17 + auth):
//   - Senhas só existem no estado do formulário (memória) e no corpo do POST. Nunca em
//     storage, log, URL ou cache persistido. O formulário é zerado ao desmontar e ao concluir.
//   - O erro "senha temporária errada" é 422 (não 401): não dispara refresh/logout.
//   - 429: aviso de espera claro e botão travado (o servidor segue sendo quem limita).
//   - O estado "pendente" é cosmético; quem barra é o servidor (403 em todo o resto).
//
// Texto em português simples: o público são vendedores, gestores e diretores.
// =============================================================================

import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import iconeUrl from '../../assets/brand/icone-bp.png';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { ThemeToggle } from '../../components/ui/ThemeToggle';
import { useAuthStore } from '../../lib/auth-store';
import { cn } from '../../lib/cn';
import { normalizePersonalEmail } from '../account/personalEmail';

import {
  FIRST_ACCESS_FORM_DEFAULTS,
  firstAccessFormSchema,
  formatWait,
  mapFirstAccessError,
  normalizeEmail,
  passwordRules,
  passwordStrength,
  RATE_LIMIT_WINDOW_MS,
  STRENGTH_LABEL,
  STRENGTH_LEVEL,
  type FirstAccessErrorView,
  type FirstAccessFormValues,
} from './firstAccess';
import { useAuth } from './useAuth';
import { useFirstAccessSubmit } from './useFirstAccess';

// ─── Peças pequenas ───────────────────────────────────────────────────────────

function EyeIcon({ off }: { off: boolean }): React.JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
      {off && <path d="M4 4l16 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />}
    </svg>
  );
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
  /** Botão "mostrar/ocultar senha" no canto da seção. */
  action?: React.ReactNode;
}

/** Bloco do formulário nomeado (leitor de tela anuncia o grupo pelo título). */
function Section({ title, children, action }: SectionProps): React.JSX.Element {
  const titleId = React.useId();
  return (
    <div role="group" aria-labelledby={titleId} className="flex flex-col gap-4 min-w-0">
      <div className="flex items-center justify-between gap-3">
        <h2
          id={titleId}
          className="font-sans text-xs font-semibold uppercase tracking-[0.14em] text-verde"
        >
          {title}
        </h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function ShowToggle({
  shown,
  onToggle,
}: {
  shown: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={shown}
      className={cn(
        'inline-flex items-center gap-2 min-h-[40px] px-3 -my-2 -mr-3 rounded-sm',
        'text-sm font-medium text-ink-3 transition-colors duration-fast ease',
        'hover:text-azul hover:bg-surface-hover active:bg-surface-muted',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-azul/15 focus-visible:text-azul',
      )}
    >
      <EyeIcon off={shown} />
      {shown ? 'Ocultar senhas' : 'Mostrar senhas'}
    </button>
  );
}

const SEGMENT_TONE = ['bg-warning', 'bg-success', 'bg-success'] as const;

/** Barra de 3 segmentos + frase. O texto carrega o significado; a cor só reforça. */
function StrengthMeter({ password }: { password: string }): React.JSX.Element {
  const strength = passwordStrength(password);
  const level = STRENGTH_LEVEL[strength];
  const rules = passwordRules(password);
  const filled = password.length > 0;

  return (
    <div role="group" aria-label="Regras da senha nova" className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="flex flex-1 gap-1" aria-hidden="true">
          {SEGMENT_TONE.map((tone, i) => (
            <span
              key={tone + String(i)}
              className={cn(
                'h-1.5 flex-1 rounded-pill transition-colors duration-fast ease-out',
                i < level ? tone : 'bg-surface-muted',
              )}
            />
          ))}
        </div>
        <span className="text-xs font-medium text-ink-2" aria-live="polite">
          {STRENGTH_LABEL[strength]}
        </span>
      </div>
      <ul className="flex flex-col gap-1">
        {rules.map((rule) => (
          <li
            key={rule.id}
            className={cn(
              'flex items-center gap-2 text-xs',
              rule.ok ? 'text-success' : filled ? 'text-ink-2' : 'text-ink-3',
            )}
          >
            <span aria-hidden="true" className="w-4 text-center font-mono">
              {rule.ok ? '✓' : '○'}
            </span>
            {rule.label}
            <span className="sr-only">{rule.ok ? ' — cumprida' : ' — falta cumprir'}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface NoticeProps {
  view: FirstAccessErrorView;
  waitLeftMs: number;
  onRelogin: () => void;
}

function Notice({ view, waitLeftMs, onRelogin }: NoticeProps): React.JSX.Element {
  return (
    <div
      role="alert"
      className="rounded-sm border border-danger bg-danger/10 px-4 py-3 text-sm text-danger flex flex-col gap-2"
    >
      <p>{view.message}</p>
      {view.kind === 'wait' && waitLeftMs > 0 && (
        <p className="font-medium">Dá para tentar de novo em {formatWait(waitLeftMs)}.</p>
      )}
      {view.kind === 'relogin' && (
        <button
          type="button"
          onClick={onRelogin}
          className="self-start min-h-[40px] font-semibold underline underline-offset-4"
        >
          Voltar ao login
        </button>
      )}
    </div>
  );
}

// ─── Formulário ───────────────────────────────────────────────────────────────

function FirstAccessForm(): React.JSX.Element {
  const { logout } = useAuth();
  const submit = useFirstAccessSubmit();
  const [showPasswords, setShowPasswords] = React.useState(false);
  const [notice, setNotice] = React.useState<FirstAccessErrorView | null>(null);
  const [lockedUntil, setLockedUntil] = React.useState<number | null>(null);
  const [now, setNow] = React.useState(() => Date.now());

  const {
    register,
    handleSubmit,
    setError,
    setFocus,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FirstAccessFormValues>({
    resolver: zodResolver(firstAccessFormSchema),
    defaultValues: FIRST_ACCESS_FORM_DEFAULTS,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  // Foco no primeiro campo ao abrir a tela.
  React.useEffect(() => {
    setFocus('currentPassword');
  }, [setFocus]);

  // Senhas nunca sobrevivem à tela: zera o formulário e o estado da mutation ao sair.
  const resetSubmit = submit.reset;
  React.useEffect(
    () => () => {
      reset(FIRST_ACCESS_FORM_DEFAULTS);
      resetSubmit();
    },
    [reset, resetSubmit],
  );

  // Relógio do aviso de espera (429): só corre enquanto o botão está travado.
  React.useEffect(() => {
    if (lockedUntil === null) return;
    const id = window.setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= lockedUntil) setLockedUntil(null);
    }, 15_000);
    return () => window.clearInterval(id);
  }, [lockedUntil]);

  const newPassword = watch('newPassword');
  const waitLeftMs = lockedUntil === null ? 0 : Math.max(0, lockedUntil - now);
  const locked = waitLeftMs > 0;
  const busy = submit.isPending || isSubmitting;
  const passwordType = showPasswords ? 'text' : 'password';

  const onSubmit = handleSubmit(async (values) => {
    // Campos voltam a type=password ANTES de enviar: senha em type=text pode ir para o
    // histórico de autopreenchimento do navegador.
    setShowPasswords(false);
    setNotice(null);
    try {
      await submit.mutateAsync({
        currentPassword: values.currentPassword,
        email: normalizeEmail(values.email),
        newPassword: values.newPassword,
        personalEmail: normalizePersonalEmail(values.personalEmail),
      });
      reset(FIRST_ACCESS_FORM_DEFAULTS);
    } catch (error: unknown) {
      const view = mapFirstAccessError(error);
      if (view.kind === 'field') {
        setError(view.field, { type: 'server', message: view.message }, { shouldFocus: true });
        return;
      }
      if (view.kind === 'wait') {
        const t = Date.now();
        setNow(t);
        setLockedUntil(t + RATE_LIMIT_WINDOW_MS);
      }
      setNotice(view);
    }
  });

  return (
    <form
      method="post"
      onSubmit={(e) => {
        void onSubmit(e);
      }}
      noValidate
      className="flex flex-col gap-6"
    >
      {notice && <Notice view={notice} waitLeftMs={waitLeftMs} onRelogin={() => void logout()} />}

      <Section
        title="1. Confirme que é você"
        action={<ShowToggle shown={showPasswords} onToggle={() => setShowPasswords((v) => !v)} />}
      >
        <Input
          id="fa-current-password"
          label="Senha temporária"
          type={passwordType}
          autoComplete="current-password"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          hint="A senha que você recebeu para entrar pela primeira vez."
          error={errors.currentPassword?.message}
          {...register('currentPassword')}
        />
      </Section>

      <Section title="2. Seu e-mail">
        <Input
          id="fa-email"
          label="E-mail"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          hint="Use um e-mail só seu. É com ele que você vai entrar no sistema."
          error={errors.email?.message}
          {...register('email', { deps: ['emailConfirm'] })}
        />
        <Input
          id="fa-email-confirm"
          label="Digite o e-mail de novo"
          type="email"
          inputMode="email"
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          error={errors.emailConfirm?.message}
          {...register('emailConfirm')}
        />
      </Section>

      <Section title="3. Sua senha nova">
        <Input
          id="fa-new-password"
          label="Senha nova"
          type={passwordType}
          autoComplete="new-password"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          error={errors.newPassword?.message}
          {...register('newPassword', { deps: ['newPasswordConfirm'] })}
        />
        <StrengthMeter password={newPassword} />
        <Input
          id="fa-new-password-confirm"
          label="Digite a senha nova de novo"
          type={passwordType}
          autoComplete="new-password"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          error={errors.newPasswordConfirm?.message}
          {...register('newPasswordConfirm')}
        />
      </Section>

      <Section title="4. Seu e-mail pessoal">
        <Input
          id="fa-personal-email"
          label="E-mail pessoal (para quem atende clientes)"
          type="email"
          inputMode="email"
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder="nome@gmail.com"
          hint="Agentes, supervisores, gestores e administradores informam aqui, para o sistema não confundir o seu e-mail com o de um cliente. Se o seu perfil é outro, pode deixar em branco. Se precisar e você pular, o sistema pede logo depois de entrar."
          error={errors.personalEmail?.message}
          {...register('personalEmail')}
        />
      </Section>

      <Button
        type="submit"
        variant="primary"
        size="lg"
        className="w-full"
        disabled={busy || locked}
        aria-busy={busy}
      >
        {busy ? 'Salvando…' : locked ? 'Aguarde para tentar de novo' : 'Salvar e entrar no sistema'}
      </Button>
    </form>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────

/** Primeiro nome para a saudação (vazio se o nome não vier). */
function firstName(fullName: string | undefined): string {
  return fullName?.trim().split(/\s+/)[0] ?? '';
}

/**
 * Tela de primeiro acesso. Fica fora do AppLayout (sem menu, sem barra do sistema):
 * só a marca, o tema e o "Sair". O AuthGuard (surface="first-access") garante que
 * só usuário autenticado e pendente chega até aqui.
 */
export function FirstAccessPage(): React.JSX.Element {
  const { logout } = useAuth();
  const name = useAuthStore((s) => firstName(s.user?.fullName));

  return (
    <div className="min-h-screen relative z-[1] flex flex-col">
      <header className="flex items-center justify-between px-5 py-4">
        <img
          src={iconeUrl}
          alt="Banco do Povo de Rondônia"
          loading="eager"
          style={{ height: '40px', width: 'auto', objectFit: 'contain' }}
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void logout()}
            className={cn(
              'min-h-[40px] min-w-[40px] px-3 rounded-sm text-sm font-medium text-ink-2',
              'transition-colors duration-fast ease hover:text-azul hover:bg-surface-hover',
              'active:bg-surface-muted',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-azul/15',
            )}
          >
            Sair
          </button>
          <ThemeToggle />
        </div>
      </header>

      <main className="flex flex-1 items-start sm:items-center justify-center px-4 pb-10">
        <div
          className="w-full max-w-[480px]"
          style={{ animation: 'fade-up var(--dur-slow) var(--ease-out) both' }}
        >
          <div
            className="bg-surface-1 rounded-lg border border-border"
            style={{ boxShadow: 'var(--elev-3)', padding: 'clamp(20px, 5vw, 40px)' }}
          >
            <div className="mb-6 flex flex-col gap-2">
              <h1
                className="font-display font-bold text-ink"
                style={{
                  fontSize: 'var(--text-2xl)',
                  letterSpacing: '-0.028em',
                  fontVariationSettings: "'opsz' 24",
                }}
              >
                {name ? `Olá, ${name}! Vamos começar` : 'Vamos começar'}
              </h1>
              <p className="text-sm text-ink-2">
                Você entrou com uma senha temporária. Para usar o sistema, escolha o seu e-mail e
                crie uma senha que só você conhece. Leva menos de um minuto.
              </p>
            </div>

            <FirstAccessForm />
          </div>

          <p className="text-center text-xs text-ink-4 mt-6 font-mono">
            Banco do Povo · Rondônia © {new Date().getFullYear()}
          </p>
        </div>
      </main>
    </div>
  );
}
